import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { PlusIcon, EyeIcon, ChevronUpIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { CubeIcon, CubeTransparentIcon } from '@heroicons/react/24/outline'
import { assetsApi, type AssetWithRelations } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'
import { AssetForm } from '../../components/forms/AssetForm'
import { AssetStatusBadge } from '../../components/ui/AssetStatusBadge'
import { Pagination } from '../../components/ui/Pagination'
import type { AssetInput, AssetStatus } from '../../../shared/types'

const STATUSES = ['active', 'maintenance', 'decommissioned'] as const
type SortField = 'serialNumber' | 'account' | 'status' | 'productLine' | 'createdAt'
type SortDirection = 'asc' | 'desc'

const STATUS_ORDER: Record<AssetStatus, number> = {
  active: 0,
  maintenance: 1,
  decommissioned: 2,
}

export function AssetsListPage() {
  const { t } = useTranslation()
  const [showForm, setShowForm] = useState(false)
  const [statusFilter, setStatusFilter] = useState<AssetStatus | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const queryClient = useQueryClient()

  // Build filters object for API
  const filters: Record<string, string> = {}
  if (statusFilter !== 'all') filters.status = statusFilter
  if (searchQuery) filters.search = searchQuery

  const { data: response, isLoading } = useQuery({
    queryKey: ['assets', { page, limit, filters }],
    queryFn: () => assetsApi.list({ page, limit, filters: Object.keys(filters).length > 0 ? filters : undefined }),
  })

  const { data: stats } = useQuery({
    queryKey: ['assets', 'stats'],
    queryFn: () => assetsApi.getStats(),
  })

  const assets = response?.data

  // Handle filter changes - reset to page 1
  const handleStatusFilter = (status: AssetStatus | 'all') => {
    setStatusFilter(status)
    setPage(1)
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    setPage(1)
  }

  const createMutation = useMutation({
    mutationFn: assetsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      setShowForm(false)
    },
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'createdAt' ? 'desc' : 'asc')
    }
  }

  // Client-side sorting
  const sortedAssets = useMemo(() => {
    if (!assets) return []

    const result = [...assets]

    result.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'serialNumber':
          comparison = a.serialNumber.localeCompare(b.serialNumber)
          break
        case 'account':
          const nameA = a.account?.name || ''
          const nameB = b.account?.name || ''
          comparison = nameA.localeCompare(nameB)
          break
        case 'status':
          comparison = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
          break
        case 'productLine':
          comparison = (a.productLine || '').localeCompare(b.productLine || '')
          break
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [assets, sortField, sortDirection])

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field
    return (
      <th
        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col -space-y-1">
            <ChevronUpIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
            <ChevronDownIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
          </div>
        </div>
      </th>
    )
  }

  if (isLoading) return <div className="p-8">{t('common.loading')}</div>

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.assets.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('pages.assets.description')}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            {showForm ? t('common.cancel') : t('pages.assets.addButton')}
          </button>

          {showForm && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.assets.newTitle')}</h3>
              <AssetForm
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setShowForm(false)}
                loading={createMutation.isPending}
              />
            </div>
          )}

          {/* Search */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.search')}</h3>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('pages.assets.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('fields.status')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleStatusFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {STATUSES.map(status => (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    statusFilter === status
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(`status.${status}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.assets.stats.title')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('status.active')}</dt>
                <dd className="font-medium text-gray-900">{stats?.active || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('status.maintenance')}</dt>
                <dd className="font-medium text-gray-900">{stats?.maintenance || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('status.decommissioned')}</dt>
                <dd className="font-medium text-gray-900">{stats?.decommissioned || 0}</dd>
              </div>
              <div className="flex justify-between text-sm border-t pt-2">
                <dt className="text-gray-500">{t('common.total')}</dt>
                <dd className="font-medium text-gray-900">{stats?.total || 0}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.assets.title')}</h1>
            <button
              type="button"
              onClick={() => setShowForm(!showForm)}
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              <PlusIcon className="h-5 w-5" />
              {showForm ? t('common.cancel') : t('pages.assets.addButton')}
            </button>
            {showForm && (
              <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <h2 className="text-lg font-medium mb-4">{t('pages.assets.newTitle')}</h2>
                <AssetForm
                  onSubmit={(data) => createMutation.mutate(data)}
                  onCancel={() => setShowForm(false)}
                  loading={createMutation.isPending}
                />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <SortableHeader field="serialNumber">{t('pages.assets.tableHeaders.serialNumber')}</SortableHeader>
                  <SortableHeader field="account">{t('pages.assets.tableHeaders.account')}</SortableHeader>
                  <SortableHeader field="productLine">{t('pages.assets.tableHeaders.productLine')}</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.assets.tableHeaders.model')}</th>
                  <SortableHeader field="status">{t('fields.status')}</SortableHeader>
                  <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900" title={t('twin.asset.tabTitle')}>
                    <CubeTransparentIcon className="h-5 w-5 mx-auto text-gray-500" />
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('pages.assets.tableHeaders.cases')}</th>
                  <SortableHeader field="createdAt">{t('pages.assets.tableHeaders.created')}</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedAssets.map((asset) => (
                  <tr key={asset.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                      <Link to={`/assets/${asset.id}`} className="hover:text-brand-600 flex items-center gap-2">
                        <CubeIcon className="h-4 w-4 text-gray-400" />
                        {asset.serialNumber}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {asset.account ? (
                        <Link to={`/accounts/${asset.accountId}`} className="hover:text-brand-600">
                          {asset.account.name}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {asset.productLine || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {asset.model || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <AssetStatusBadge status={asset.status} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                      {asset.twinProjectId ? (
                        <Link
                          to={`/twin/${asset.twinProjectId}`}
                          className="inline-flex items-center justify-center"
                          title={t('twin.asset.tabTitle')}
                        >
                          <CubeTransparentIcon className="h-5 w-5 text-brand-500 hover:text-brand-600" />
                        </Link>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {asset._count?.serviceCases || 0}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {new Date(asset.createdAt).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <Link
                        to={`/assets/${asset.id}`}
                        className="text-brand-600 hover:text-brand-900"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                    </td>
                  </tr>
                ))}
                {sortedAssets.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-sm text-gray-500">
                      {t('pages.assets.noAssets')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {response && response.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={page}
                totalPages={response.totalPages}
                total={response.total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit)
                  setPage(1)
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
